import React from "react"
import { createRoot } from "react-dom/client"
import MessageComponent from "../components/MessageComponent"
import { MessageConfig, MessageInstance, MessageType } from "./propTypes"

let messageConfig = {
  top: 24,
  duration: 1.5,
  maxCount: Number.POSITIVE_INFINITY,
  getContainer: () => document.body,
}

let messageContainer: HTMLDivElement | null = null
let messageInstances: MessageInstance[] = []
let messageKey = 0

function getSafeAreaInset(varName: 'safe-area-inset-top' | 'safe-area-inset-bottom', fallback = 0) {
  if (typeof CSS !== 'undefined' && CSS.supports(`top: constant(${varName})`)) {
    return `constant(${varName}, ${fallback})`;
  }
  return `env(${varName}, ${fallback})`;
}

const getMessageContainer = () => {
  if (messageContainer) return messageContainer

  if (typeof window !== "undefined") {
    messageContainer = document.createElement("div")
    messageContainer.className = "fixed left-1/2 transform -translate-x-1/2 z-50 pointer-events-none w-[90vw] md:w-[80vw]"
    messageContainer.style.top = `calc(${messageConfig.top}px + ${getSafeAreaInset('safe-area-inset-top', 0)})`
    messageConfig.getContainer().appendChild(messageContainer)
  }

  return messageContainer
}

const addMessage = async (config: MessageConfig): Promise<void> => {
  const container = getMessageContainer()
  if (!container) return

  const key = config.key || `message-${messageKey++}`

  await Promise.all(
    messageInstances.map((instance) => {
      return new Promise<void>((resolve) => {
        if (instance.ref?.current) {
          instance.ref.current.close()
          setTimeout(resolve, 300)
        } else {
          resolve()
        }
      })
    })
  )

  messageInstances.forEach((instance) => {
    instance.root.unmount()
    if (instance.div.parentNode === container) {
      container.removeChild(instance.div)
    }
  })
  messageInstances = []

  return new Promise((resolve) => {
    const onUnmount = () => {
      const index = messageInstances.findIndex((inst) => inst.key === key)
      if (index !== -1) {
        const instance = messageInstances[index]
        instance.root.unmount()
        if (instance.div.parentNode === container) {
          container.removeChild(instance.div)
        }
        messageInstances.splice(index, 1)
      }
      resolve()
    }

    const duration = config.duration ?? messageConfig.duration

    const div = document.createElement("div")
    div.style.width = "100%"
    container.appendChild(div)

    const root = createRoot(div)
    const componentRef = React.createRef<{ close: () => void }>()

    messageInstances.push({ key, config, root, div, ref: componentRef })

    root.render(<MessageComponent {...config} ref={componentRef} onUnmount={onUnmount} />)

    if (duration > 0) {
      setTimeout(() => {
        const instance = messageInstances.find((i) => i.key === key)
        instance?.ref?.current?.close()
      }, duration * 1000)
    }
  })
}

const createConfig = (
  content: string | MessageConfig,
  type: MessageType,
  duration?: number,
  onClose?: () => void
): MessageConfig => {
  return typeof content === "string"
    ? { content, duration, onClose, type }
    : { ...content, type }
}

export const messageObject = {
  open: (config: MessageConfig | string) => {
    const finalConfig = typeof config === "string" ? { content: config } : config
    return addMessage(finalConfig)
  },

  success: (content: string | MessageConfig, duration?: number, onClose?: () => void) =>
    addMessage(createConfig(content, "success", duration, onClose)),

  error: (content: string | MessageConfig, duration?: number, onClose?: () => void) =>
    addMessage(createConfig(content, "error", duration, onClose)),

  info: (content: string | MessageConfig, duration?: number, onClose?: () => void) =>
    addMessage(createConfig(content, "info", duration, onClose)),

  warning: (content: string | MessageConfig, duration?: number, onClose?: () => void) =>
    addMessage(createConfig(content, "warning", duration, onClose)),

  warn: (content: string | MessageConfig, duration?: number, onClose?: () => void) =>
    messageObject.warning(content, duration, onClose),

  loading: (content: string | MessageConfig, duration?: number, onClose?: () => void) =>
    addMessage(createConfig(content, "loading", duration, onClose)),

  config: (options: {
    top?: number
    duration?: number
    maxCount?: number
    getContainer?: () => HTMLElement
  }) => {
    messageConfig = { ...messageConfig, ...options }

    if (messageContainer && options.top !== undefined) {
      messageContainer.style.top = `${options.top}px`
    }
  },

  destroy: () => {
    if (messageContainer) {
      messageInstances.forEach((instance) => {
        instance.root.unmount()
      })

      messageContainer.parentNode?.removeChild(messageContainer)
      messageContainer = null
      messageInstances = []
    }
  },
}

export default messageObject;